import _ from 'lodash';
import { chartTypes, isInverted, normalizedType } from 'config/constants';

export const findVisibleHighChartObject = messageId =>
  Highcharts.charts.find(x => x?.renderTo.id.includes(messageId) && x.renderTo.getBoundingClientRect().width > 0);

/**
 * Returns the options of the specified table chart JSON.
 */
export const getTableOptions = tableJson => _.pick(tableJson, ['columns']);

/**
 * Merges all Visualization Options applied at various levels
 *
 * Contrary to 'mergeChartOverrides', the method only merges the chart overrides from visualization options and the
 * modifications, and does not consider the original chart options. This is needed to compute the "diff", i.e. for a
 * chart override being created when e.g. sharing an answer or saving a widget with visualization modifications.
 *
 * @param visualizationType the type of the visualization
 * @param originalVisualizationOptions visualizationOptions coming with the visualization (currently only metaColumns
 *                                     for Highcharts charts and stripped-down columns for table charts)
 * @param customVisualizationOptions visualizationOptions that are stored in the backend e.g. on the widget or shared answer level, includes previous session modifications
 * @param modifications is an array of "modifiedVisualizations" (as in the redux state usually), i.e. modifications in the session
 * @returns {Object}
 */
export const mergeVisualizationOptions = (
  visualizationType,
  originalVisualizationOptions,
  customVisualizationOptions,
  modifications
) => {
  try {
    const mergedChartOverride = mergeChartOverrides(
      visualizationType,
      originalVisualizationOptions,
      customVisualizationOptions,
      modifications
    );

    return {
      // Do not do a deep merge here, only for the chartOverride. Unclear anyways if we want to preserve the custom options
      // coming from widgets or always save the newly generated ones. At this time we only really need to preserve chartOverride.
      ...originalVisualizationOptions,
      ...customVisualizationOptions,
      ...(Object.keys(mergedChartOverride).length > 0 && { chartOverride: mergedChartOverride })
    };
  } catch (error) {
    console.error('Error merging visualization options', error);
    return {};
  }
};

/**
 * Merges all chart overrides applied at the various levels.
 *
 * If `chartOptions` are defined, the chart overrides from the specified visualization options are merged into them,
 * else they are merged into an empty object. The specified modifications are then merged into the result of this
 * operation. If the modifications contain manual ones (i.e. with 'mergeWithPrevious' set to false), then modifications
 * up to and including the last manual one are skipped. This is because manual modifications contain the merge of all
 * preceding modifications up to this point.
 *
 * @param visualizationType the type of the visualization
 * @param originalVisualizationOptions visualizationOptions coming with the visualization (currently only metaColumns
 *                                     for Highcharts charts and stripped-down columns for table charts)
 * @param customVisualizationOptions visualizationOptions that are stored in the backend e.g. on the widget or shared answer level, includes previous session modifications
 * @param modifications is an array of "modifiedVisualizations" (as in the redux state usually), i.e. modifications in the session
 * @param chartOptions the base Highcharts or table chart options (optional, see above)
 * @returns {Object}
 */
export const mergeChartOverrides = (
  visualizationType,
  originalVisualizationOptions,
  customVisualizationOptions,
  modifications,
  chartOptions
) => {
  try {
    // use the merge customizer for table charts, otherwise use the default merge logic
    const mergeCustomizer = visualizationType === chartTypes.TABLE_CHART ? tableChartMergeCustomizer : undefined;

    // include the original and custom visualization options in the modifications
    modifications = [
      { chartOverride: originalVisualizationOptions?.chartOverride || {}, mergeWithPrevious: true },
      { chartOverride: customVisualizationOptions?.chartOverride || {}, mergeWithPrevious: true },
      ...modifications
    ];
    // find the first modification after the last manual one (which has 'mergeWithPrevious' set to false), if any:
    // since manual modifications are done via the code dialog and contain the merge of all previous modifications,
    // we can discard the modifications before it
    const startIndex = Math.max(
      0,
      modifications.findLastIndex(modification => !modification.mergeWithPrevious)
    );
    // merge the modifications using the chart options as the base (if any are provided)
    return modifications.slice(startIndex).reduce(
      // merge into a new object to avoid modifying the original chart options
      (acc, cur) => _.mergeWith({}, acc, cur.chartOverride, mergeCustomizer),
      chartOptions
    );
  } catch (error) {
    console.error('Error merging chart overrides', error);
    return {};
  }
};

/**
 * A merge customizer function to merge table chart options.
 *
 * The function will be called (potentially multiple times) in 'mergeChartOverrides' with the following parameters:
 *    - base: initially the table chart options (potentially empty), subsequently the current / accumulated merged object
 *    - modification: the table modification (diff / delta) object currently being merged into the base object
 *
 * The function implements the following merge logic for the 'columns' property:
 *  - the order of columns of the modifications object is used
 *  - individual columns are matched and merged based on the column identifier
 *  - only the columns from the base object are used:
 *    - columns from the modification object which are not present in the base object are discarded
 *    - columns from the base object which are not present in the modification object are added at the end of the columns list
 *
 * This merge logic is needed since the default merge logic merges array objects based on the position / index. For
 * properties other thant 'columns', the function returns `undefined`, which causes the default merge logic to be used.
 *
 * @see https://lodash.com/docs/4.17.15#mergeWith
 */
const tableChartMergeCustomizer = (base, modification, key, obj, src, stack) => {
  // check that we are merging the top-level 'columns' property and that it is defined in both objects
  if (key === 'columns' && stack.size === 0 && _.isArray(base) && _.isArray(modification)) {
    // use the columns and order of the modification object
    const mergedColumns = modification.flatMap(modificationColumn => {
      // find the corresponding column in the base object
      const baseColumn = base.find(baseColumn => baseColumn.identifier === modificationColumn.identifier);
      return baseColumn
        ? // if it exists, override the properties of the base column with the modified column
          [
            {
              ...baseColumn,
              ...modificationColumn,
              // disallow overriding the type
              type: baseColumn.type
            }
          ]
        : // if it doesn't exist, drop the column
          [];
    });
    // find the columns from the base object that aren't present in the columns from the modification object
    const missingColumns = base.filter(column => !mergedColumns.some(c => c.identifier === column.identifier));
    // add the missing columns at the end of the merged columns
    return [...mergedColumns, ...missingColumns];
  }
  // else return undefined, which causes the default merge logic to be used,
  // cf. https://lodash.com/docs/4.17.15#mergeWith
};

/** Merges visualizations with both normal & inverted versions, using the one matching isChartInverted */
export const adaptVisualizations = (visualizations, isChartInverted) => {
  const [invertedVisualizations, baseVisualizations] = _.partition(visualizations, item => isInverted(item.type));

  return baseVisualizations.map(baseVisualization => {
    let adaptedVisualization = baseVisualization;

    invertedVisualizations
      .filter(invertedVisualization => baseVisualization.type === normalizedType(invertedVisualization.type))
      .forEach(invertedVisualization => {
        if (isChartInverted) {
          adaptedVisualization = invertedVisualization;
        }
        adaptedVisualization.allowInvert = true;
      });

    return adaptedVisualization;
  });
};
